import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
    Book, 
    Code, 
    PlayArrow, 
    QuestionAnswer, 
    School, 
    Rocket,
    Article,
    Description,
    Lightbulb,
    VideoLibrary
} from '@mui/icons-material';

const HelpCenter = () => {
    const resources = [
        {
            title: 'Guide de Démarrage Rapide',
            description: 'Intégrez Netikash en 15 minutes avec notre guide étape par étape',
            icon: Rocket,
            link: '/integration',
            color: 'from-amber-500 to-orange-500',
            bgColor: 'bg-amber-50',
            borderColor: 'border-amber-200'
        },
        {
            title: 'Documentation API',
            description: 'Documentation complète avec tous les endpoints, paramètres et exemples',
            icon: Book,
            link: '/documentation',
            color: 'from-blue-500 to-indigo-500',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200'
        },
        {
            title: 'Exemples de Code',
            description: 'Exemples pratiques dans différents langages de programmation',
            icon: Code,
            link: '/examples',
            color: 'from-purple-500 to-pink-500',
            bgColor: 'bg-purple-50',
            borderColor: 'border-purple-200'
        },
        {
            title: 'FAQ',
            description: 'Réponses aux questions fréquemment posées',
            icon: QuestionAnswer,
            link: '/faq',
            color: 'from-green-500 to-emerald-500',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200'
        },
        {
            title: 'Tutoriels Vidéo',
            description: 'Vidéos explicatives pour apprendre à utiliser l\'API',
            icon: VideoLibrary,
            link: '#',
            color: 'from-red-500 to-rose-500',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200',
            comingSoon: true
        },
        {
            title: 'Articles et Guides',
            description: 'Articles de blog et guides détaillés sur des sujets spécifiques',
            icon: Article,
            link: '/blog',
            color: 'from-teal-500 to-cyan-500',
            bgColor: 'bg-teal-50',
            borderColor: 'border-teal-200'
        },
        {
            title: 'Playground API',
            description: 'Testez les endpoints API directement depuis votre navigateur',
            icon: PlayArrow,
            link: '/examples',
            color: 'from-orange-500 to-amber-500',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200'
        },
        {
            title: 'Bonnes Pratiques',
            description: 'Meilleures pratiques de sécurité et d\'intégration',
            icon: Lightbulb,
            link: '/security',
            color: 'from-yellow-500 to-amber-500',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200'
        }
    ];

    const learningPaths = [
        {
            title: 'Débutant',
            steps: [
                { text: 'Créer un compte développeur', link: 'https://gateway.netikash.com' },
                { text: 'Lire le guide de démarrage', link: '/integration' },
                { text: 'Essayer votre premier paiement', link: '/examples?tab=payment' }
            ],
            icon: School,
            color: 'blue'
        },
        {
            title: 'Intermédiaire',
            steps: [
                { text: 'Configurer les webhooks', link: '/documentation?section=webhooks' },
                { text: 'Implémenter la vérification', link: '/documentation?section=payment-verification' },
                { text: 'Gérer les erreurs', link: '/error-codes' }
            ],
            icon: Description,
            color: 'purple'
        },
        {
            title: 'Avancé',
            steps: [
                { text: 'Optimiser les performances', link: '/documentation?section=rate-limits' },
                { text: 'Implémenter l\'idempotence', link: '/documentation?section=idempotency' },
                { text: 'Best practices de sécurité', link: '/security' }
            ],
            icon: Rocket,
            color: 'amber'
        }
    ];

    return (
        <>
            <Helmet>
                <title>Centre d'Aide - Netikash Developer Portal</title>
                <meta name="description" content="Centre d'aide Netikash : guides, tutoriels, documentation et ressources pour intégrer l'API Netikash." />
                <meta property="og:title" content="Centre d'Aide - Netikash" />
                <link rel="canonical" href="https://dev.netikash.com/help-center" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                                <School className="mr-2" />
                                <span className="font-semibold text-lg">CENTRE D'AIDE</span>
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                                Ressources et Guides
                            </h1>
                            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                                Tout ce dont vous avez besoin pour intégrer et utiliser l'API Netikash efficacement
                            </p>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Resources Grid */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">Ressources Principales</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {resources.map((resource, index) => {
                                const Icon = resource.icon;
                                const isExternal = resource.link.startsWith('http');
                                const Component = isExternal || resource.link === '#' ? 'div' : Link;
                                
                                return (
                                    <Component
                                        key={index}
                                        to={!isExternal && resource.link !== '#' ? resource.link : undefined}
                                        href={isExternal ? resource.link : undefined}
                                        className={`${resource.bgColor} ${resource.borderColor} border-2 rounded-2xl p-6 hover:shadow-lg transition-all transform hover:-translate-y-1 ${
                                            resource.link === '#' ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                                        }`}
                                    >
                                        <div className={`w-12 h-12 bg-gradient-to-r ${resource.color} rounded-xl flex items-center justify-center mb-4`}>
                                            <Icon className="text-white text-2xl" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">{resource.title}</h3>
                                        <p className="text-gray-700 text-sm mb-3">{resource.description}</p>
                                        {resource.comingSoon && (
                                            <span className="inline-block px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                                                Bientôt disponible
                                            </span>
                                        )}
                                    </Component>
                                );
                            })}
                        </div>
                    </section>

                    {/* Learning Paths */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">Parcours d'Apprentissage</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {learningPaths.map((path, index) => {
                                const Icon = path.icon;
                                const colorClasses = {
                                    blue: 'bg-blue-100 text-blue-700',
                                    purple: 'bg-purple-100 text-purple-700',
                                    amber: 'bg-amber-100 text-amber-700'
                                };
                                
                                return (
                                    <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                                        <div className={`w-12 h-12 ${colorClasses[path.color]} rounded-xl flex items-center justify-center mb-4`}>
                                            <Icon />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-4">{path.title}</h3>
                                        <ul className="space-y-3">
                                            {path.steps.map((step, stepIndex) => {
                                                const isExternal = step.link.startsWith('http');
                                                return (
                                                    <li key={stepIndex} className="flex items-start space-x-3">
                                                        <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-sm font-semibold text-gray-700">
                                                            {stepIndex + 1}
                                                        </span>
                                                        {isExternal ? (
                                                            <a
                                                                href={step.link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-gray-700 hover:text-amber-600 hover:underline flex-1"
                                                            >
                                                                {step.text}
                                                            </a>
                                                        ) : (
                                                            <Link
                                                                to={step.link}
                                                                className="text-gray-700 hover:text-amber-600 hover:underline flex-1"
                                                            >
                                                                {step.text}
                                                            </Link>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Quick Links */}
                    <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Liens Rapides</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { title: 'Créer un compte', link: 'https://gateway.netikash.com', external: true },
                                { title: 'Statut des services', link: '/status' },
                                { title: 'Changelog API', link: '/changelog' },
                                { title: 'Support technique', link: '/technical-support' }
                            ].map((item, index) => {
                                const Component = item.external ? 'a' : Link;
                                return (
                                    <Component
                                        key={index}
                                        to={!item.external ? item.link : undefined}
                                        href={item.external ? item.link : undefined}
                                        target={item.external ? '_blank' : undefined}
                                        rel={item.external ? 'noopener noreferrer' : undefined}
                                        className="bg-white border border-amber-200 rounded-lg p-4 hover:shadow-md transition-all text-center"
                                    >
                                        <span className="text-gray-900 font-medium">{item.title}</span>
                                    </Component>
                                );
                            })}
                        </div>
                    </section>

                    {/* Support CTA */}
                    <section className="mt-12 text-center">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Besoin d'aide supplémentaire ?
                            </h3>
                            <p className="text-gray-700 mb-6">
                                Notre équipe de support est disponible pour vous aider avec vos questions techniques.
                            </p>
                            <div className="flex flex-wrap gap-4 justify-center">
                                <Link
                                    to="/contact"
                                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                                >
                                    Nous contacter
                                </Link>
                                <Link
                                    to="/technical-support"
                                    className="px-6 py-3 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium hover:bg-amber-50 transition-all"
                                >
                                    Support technique
                                </Link>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default HelpCenter;